import React, { useState } from "react"
import ContentBrowser from "aldoo-ra/CMS/browsers/content-browser"
import { previewImage } from "aldoo-ra/CMS/preview"
import Tooltip from "aldoo-ra/Tooltip"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Controller from "aldoo-ra/CMS/controllers/cms-controller"
import { getPreSaveFormatter } from "aldoo-ra/CMS/content-pre-save-formatter"
import { showNotification } from "aldoo-ra/Notification"
import { EyeIcon } from "@heroicons/react/24/outline" // Import the eye icon

export default function BlogPostBrowser() {
  // Initialize CMS controller
  const controller = Controller()
  
  // State to trigger ContentBrowser refresh via callbacks
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Function to handle preview action
  const handlePreview = (post) => {
    // Use the post's slug for the URL
    const slug = post.content.slug || post._id // Fallback to ID if slug doesn't exist
    window.open(`/blog/${slug}`, "_blank")
  }

  // Status options for the dropdown with descriptions
  const statusOptions = [
    { 
      value: "draft", 
      label: "Draft", 
      name: "Draft",
      description: "Not visible publicly, work in progress"
    },
    { 
      value: "preview", 
      label: "Preview", 
      name: "Preview",
      description: "Visible to admins, not indexed by SEO"
    },
    { 
      value: "published", 
      label: "Published", 
      name: "Published",
      description: "Public visibility, SEO aware"
    }
  ]

  // Function to get status colors for white background buttons
  const getStatusColors = (status) => {
    switch (status) {
      case "published":
        return "text-admin_primary"
      case "preview":
        return "text-admin_warning"
      case "draft":
        return "text-admin_neutral_3"
      default:
        return "text-admin_warning"
    }
  }

  // Function to get status colors for dropdown options (colored backgrounds)
  const getDropdownStatusColors = (status) => {
    switch (status) {
      case "published":
        return "bg-admin_primary text-white"
      case "preview":
        return "bg-admin_warning text-white"
      case "draft":
        return "bg-admin_neutral_3 text-white"
      default:
        return "bg-admin_warning text-white"
    }
  }

  // Custom renderer for dropdown options with descriptions
  const renderStatusOption = (option) => (
    <div className="w-full flex flex-col py-2 cursor-pointer">
      <div className="flex items-center mb-1">
        <span className={`${getDropdownStatusColors(option.value)} rounded-full px-3 py-1 text-sm font-medium`}>
          {option.value === 'draft' ? 'Draft' : option.value === 'preview' ? 'Preview' : 'Published'}
        </span>
      </div>
      <div className="text-xs text-admin_text_dim pl-1">
        {option.description}
      </div>
    </div>
  )

  // Custom renderer for selected value display - return JSX with colored badge
  const renderSelectedValue = (option) => {
    if (!option) return <span className="text-admin_text_secondary">Select status</span>
    return (
      <span className={`${getDropdownStatusColors(option.value)} rounded-full px-3 py-1 text-xs font-medium`}>
        {option.label}
      </span>
    )
  }

  // Function to handle status change and auto-save
  const handleStatusChange = async (item, newStatusArray) => {
    if (newStatusArray.length === 0) return // Don't allow empty selection
    
    const newStatus = newStatusArray[0].value
    if (newStatus === item.content.status) return // No change needed
    
    try {
      // Update the item's content status
      let updatedContent = {
        ...item.content,
        status: newStatus
      }
      
      // Apply pre-save formatter if available (like ContentBrowser does)
      const preFormatter = getPreSaveFormatter(updatedContent?.type)
      if (preFormatter) {
        updatedContent = preFormatter(updatedContent)
      }
      
      // Remove _id from content if it exists (as per ContentBrowser logic)
      if (updatedContent._id) delete updatedContent._id
      
      // Use the controller to save content (same as ContentBrowser)
      const response = await controller.saveContent({
        payload: {
          ...item,
          content: updatedContent,
        },
      })
      
      // Check if the response indicates success (backend returns {result: id} on success)
      if (response && response.result) {
        // Update the item's status immediately in local state
        item.content.status = newStatus
        
        // Trigger a refresh of the content browser to get fresh data from server
        setRefreshTrigger(prev => prev + 1)
        
        // Show success notification
        showNotification({
          type: 'success',
          message: `Status updated to ${newStatus}`,
          duration: 3000
        })
      } else {
        throw new Error(response?.error || 'Save operation failed')
      }
    } catch (error) {
      console.error('Error updating blog post status:', error)
      showNotification({
        type: 'error',
        message: 'Failed to update status. Please try again.',
        duration: 5000
      })
    }
  }

  return (
    <ContentBrowser
      contentType="BlogPost"
      contentTitle="Blog Posts"
      addButtonText="Add"
      allowTypeSelection={false}
      allowTypeBrowsing={false}
      showPreview={true}
      searchFields="content.title"
      sort={{ createdAt: -1 }}
      externalRefreshTrigger={refreshTrigger}
      localUserPreferencesKey="admin.blog.posts"
      additionalColumns={[
        {
          id: "title",
          title: "Title",
          headerClassName: "w-[40%]",
          render: (value, item) => {
            const preview = previewImage(item)
            return (
              <div className="flex flex-row items-center">
                {preview && (
                  <Tooltip
                    content={
                      <div className="flex text-center">
                        <span className="font-bold text-admin_text">
                          {item?.content.label}
                        </span>

                        <img
                          src={preview}
                          className="w-[400px] h-[400px] object-contain mr-2 rounded-[10px]"
                        />
                      </div>
                    }
                    position="right"
                  >
                    <img
                      src={preview}
                      className="w-10 h-10 rounded-[10px] min-w-10 min-h-10 flex-shrink-0 mr-2"
                    />
                  </Tooltip>
                )}

                <span 
                  className="text-admin_text ml-2 cursor-pointer hover:text-admin_primary transition-colors"
                  onClick={() => {
                    if (item && item._id && item.onEdit) {
                      item.onEdit(item)
                    }
                  }}
                >
                  {item?.content.title}
                </span>
              </div>
            )
          },
        },
        {
          id: "published",
          title: "Status",
          headerClassName: "w-[20%] text-center",
          render: (value, item) => {
            // Find the current status option
            const currentStatus = statusOptions.find(option => option.value === item.content.status) || statusOptions[0]
            
            return (
              <div className="w-full flex justify-center items-center px-2">
                <DropdownSelect
                  variant="admin"
                  options={statusOptions}
                  value={[currentStatus]}
                  onChange={(selectedOptions) => handleStatusChange(item, selectedOptions)}
                  placeholder="Select status"
                  multiSelection={false}
                  enableResetButton={false}
                  showCheckbox={false}
                  showSelectionTick={true}
                  valueField="value"
                  fixedWidth="140px"
                  renderOption={renderStatusOption}
                  onValueItemRender={renderSelectedValue}
                  key={`dropdown-${item._id}`}
                  styleOverrides={{
                    container: "relative inline-block text-left w-[140px]",
                    panel: "absolute origin-top-left rounded-md shadow-xl bg-admin_paper ring-1 ring-admin_bordered focus:outline-none w-[280px]",
                    button: "inline-flex items-center justify-between rounded-md border border-admin_bordered px-2 py-1 text-xs font-medium bg-admin_paper outline-none focus:outline-none focus:border-admin_text_dim focus:shadow-none  transition-all min-h-[32px] w-[140px] text-admin_text"
                  }}
                />
              </div>
            )
          },
        },
        // Created date column
        {
          id: "created",
          title: "Created",
          headerClassName: "w-[15%]",
          render: (_, item) => (
            <span className="text-admin_text text-sm">
              {new Date(item.createdAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric"
              })}
            </span>
          ),
        },
        // Preview column
        {
          id: "preview",
          title: "Preview",
          headerClassName: "w-[10%] text-center",
          render: (_, item) => (
            <div className="flex justify-center">
              <button
                className="p-1.5 text-admin_text hover:text-admin_primary transition-colors"
                onClick={() => handlePreview(item)}
                title="Preview blog post"
              >
                <EyeIcon className="w-5 h-5" />
              </button>
            </div>
          ),
        },
      ]}
      additionalFilters={{
        "content.status": { $exists: true },
      }}
    />
  )
}
